import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Slider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PersonOutline as PersonOutlineIcon,
  AccessTime as AccessTimeIcon,
  DateRange as DateRangeIcon,
  CloudDownload as CloudDownloadIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeDown as VolumeDownIcon,
  VolumeMute as VolumeMuteIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
}));

const AudioCard = ({ audio, onDelete, onEdit, onDownload }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const audioRef = useRef(null);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateTime = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Initialize audio element only when needed (lazy loading)
  const initializeAudio = () => {
    if (!audio.file || audioRef.current) return;

    const audioElement = new Audio();
    audioElement.preload = 'none'; // Prevent automatic buffering
    audioElement.src = audio.file;
    audioRef.current = audioElement;

    // Audio event listeners
    audioElement.addEventListener('loadstart', () => {
      setIsLoading(true);
    });

    audioElement.addEventListener('canplay', () => {
      setIsLoading(false);
      setAudioLoaded(true);
      setDuration(audioElement.duration);
    });

    audioElement.addEventListener('timeupdate', updateTime);

    audioElement.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
    });

    audioElement.addEventListener('error', () => {
      setIsLoading(false);
      console.error('Audio failed to load');
    });

    audioElement.volume = volume;
    audioElement.muted = isMuted;
  };

  const togglePlayPause = () => {
    if (!audio.file) return;

    // Initialize audio on first play
    if (!audioRef.current) {
      initializeAudio();
    }

    const audioElement = audioRef.current;
    if (!audioElement) return;

    if (isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audioElement.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Audio play failed:', error);
          setIsLoading(false);
        });
    }
  };

  const handleStop = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (event, newValue) => {
    if (!audioRef.current || !duration) return;

    const seekTime = (newValue / 100) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleSeekStart = () => {
    // Disable time updates while seeking
    if (audioRef.current) {
      audioRef.current.removeEventListener('timeupdate', updateTime);
    }
  };

  const handleSeekEnd = (event, newValue) => {
    if (!audioRef.current || !duration) return;

    const seekTime = (newValue / 100) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);

    // Re-enable time updates after seeking
    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', updateTime);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    const newVolume = newValue / 100;
    setVolume(newVolume);

    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);

    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  };

  // Function to cleanup audio element
  const cleanupAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener('timeupdate', updateTime);
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setAudioLoaded(false);
    setIsLoading(false);
  };

  // Enhanced delete handler with audio cleanup
  const handleDeleteClick = () => {
    // Clean up audio first
    cleanupAudio();
    // Then trigger parent delete handler
    if (onDelete) {
      onDelete(audio);
    }
  };

  // Cleanup audio element on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  const getStatusColor = (approved, rejected) => {
    if (approved) return "success";
    if (rejected) return "error";
    return "warning";
  };

  const getStatusLabel = (approved, rejected) => {
    if (approved) return "Approved";
    if (rejected) return "Rejected";
    return "Pending";
  };

  const formatDuration = (duration) => {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return <VolumeMuteIcon />;
    if (volume < 0.5) return <VolumeDownIcon />;
    return <VolumeUpIcon />;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <StyledCard>
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "1.1rem" }}
              title={audio.title}
            >
              {audio.title}
            </Typography>
            <Chip
              label={getStatusLabel(audio.approved, audio.rejected)}
              color={getStatusColor(audio.approved, audio.rejected)}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: "primary.light" }}>
            <PersonOutlineIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" color="textSecondary" noWrap title={audio.username}>
            {audio.username}
          </Typography>
        </Box>

        {audio.duration > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
            <Typography variant="body2" color="textSecondary">
              {formatDuration(audio.duration)}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <DateRangeIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
          <Typography variant="body2" color="textSecondary">
            {formatDate(audio.created_date)}
          </Typography>
        </Box>

        {audio.file && (
          <Box sx={{ mt: 2 }}>
            {/* Audio Controls */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconButton
                onClick={togglePlayPause}
                color="primary"
                size="small"
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : isPlaying ? (
                  <PauseIcon />
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>

              <IconButton
                onClick={handleStop}
                color="primary"
                size="small"
                disabled={!audioLoaded}
              >
                <StopIcon />
              </IconButton>

              <Box sx={{ flex: 1, mx: 1 }}>
                <Slider
                  value={progress}
                  onChange={handleSeek}
                  onChangeCommitted={handleSeekEnd}
                  onMouseDown={handleSeekStart}
                  disabled={!audioLoaded}
                  size="small"
                  sx={{
                    height: 4,
                    '& .MuiSlider-thumb': {
                      width: 12,
                      height: 12,
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: '0px 0px 0px 8px rgba(25, 118, 210, 0.16)',
                      },
                    },
                  }}
                />
              </Box>

              <Typography variant="caption" color="textSecondary" sx={{ minWidth: 45 }}>
                {formatDuration(currentTime)} / {formatDuration(duration || audio.duration || 0)}
              </Typography>
            </Box>

            {/* Volume Control */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                <IconButton
                  onClick={toggleMute}
                  size="small"
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  onMouseLeave={() => setShowVolumeSlider(false)}
                >
                  {getVolumeIcon()}
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  width: showVolumeSlider ? 80 : 0,
                  transition: 'width 0.2s ease',
                  overflow: 'hidden'
                }}
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <Slider
                  value={isMuted ? 0 : volume * 100}
                  onChange={handleVolumeChange}
                  size="small"
                  sx={{ height: 4 }}
                />
              </Box>

              <Typography variant="caption" color="textSecondary">
                {audioLoaded ? 'Ready' : 'Click play to load'}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, pt: 1 }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => onEdit && onEdit(audio)}
          sx={{ borderRadius: 2 }}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteClick}
          sx={{ borderRadius: 2 }}
        >
          Delete
        </Button>
      </CardActions>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 150 } }}
      >
        <MenuItem onClick={() => { onEdit && onEdit(audio); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {audio.file && (
          <MenuItem onClick={() => { onDownload && onDownload(audio); handleMenuClose(); }}>
            <ListItemIcon>
              <CloudDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { handleDeleteClick(); handleMenuClose(); }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </StyledCard>
  );
};

export default AudioCard;